import omit from 'lodash/omit';
import zipObject from 'lodash/zipObject';
import {
	type ClearCollapsedCardGroupAction,
	type ClearExpandedCardGroupAction,
	CARD_GROUP_TOGGLE,
	CARD_LIST_DRAG_END,
	CARD_LIST_DRAG_START,
	CARD_LIST_TOGGLE,
	CARD_LISTS_DISABLE,
	CARD_LISTS_ENABLE_ALL,
	type CardListDragEndAction,
	type CardListDragStartAction,
	type CardListsDisableAction,
	type CardListsEnableAllAction,
	type CardListToggleAction,
	type CardGroupToggleAction,
	SET_COLLAPSED_CARD_LIST,
	type SetCollapsedCardListAction,
	SET_COLLAPSED_CARD_GROUP,
	type SetCollapsedCardGroupAction,
	CLEAR_COLLAPSED_CARD_GROUP,
	SET_EXPANDED_CARD_GROUP,
	type SetExpandedCardGroupAction,
	CLEAR_EXPANDED_CARD_GROUP,
	type CollapseAllCardGroupsAction,
	COLLAPSE_ALL_CARD_GROUPS,
} from '../../../actions/card-list';
import {
	type SprintCreateSuccessAction,
	SPRINT_CREATE_SUCCESS,
} from '../../../actions/sprints/create';
import type { CardListType } from './types';

const defaultState = {
	collapsed: {},
	disabled: {},
	recentCreatedId: undefined,
	dragState: {},
	isDragging: false,
	isDraggingSubtask: false,
	cardGroupCollapsed: {},
	cardGroupExpanded: {},
} as const;

type Action =
	| CardListToggleAction
	| CardListsDisableAction
	| CardListsEnableAllAction
	| SetCollapsedCardListAction
	| SprintCreateSuccessAction
	| CardListDragStartAction
	| CardListDragEndAction
	| CardGroupToggleAction
	| SetCollapsedCardGroupAction
	| ClearCollapsedCardGroupAction
	| SetExpandedCardGroupAction
	| ClearExpandedCardGroupAction
	| CollapseAllCardGroupsAction;

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (state: CardListType = defaultState, action: Action): CardListType => {
	switch (action.type) {
		case CARD_LIST_TOGGLE: {
			const { cardListId } = action.payload;
			if (state.collapsed[cardListId]) {
				return {
					...state,
					collapsed: omit(state.collapsed, cardListId),
				};
			}
			return {
				...state,
				collapsed: {
					...state.collapsed,
					[cardListId]: true,
				},
			};
		}
		case SET_COLLAPSED_CARD_LIST: {
			return {
				...state,
				collapsed: zipObject(
					action.payload.cardListIds,
					Array(action.payload.cardListIds.length).fill(true),
				),
			};
		}
		case CARD_LISTS_DISABLE: {
			const newDisabled = action.payload.cardListIds.reduce(
				// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
				(disabled, cardListId) => ({ ...disabled, [cardListId]: true }),
				{ ...state.disabled },
			);

			return {
				...state,
				disabled: newDisabled,
			};
		}
		case CARD_LISTS_ENABLE_ALL: {
			return {
				...state,
				disabled: {},
			};
		}
		case SPRINT_CREATE_SUCCESS: {
			const {
				payload: {
					sprint: { id },
				},
			} = action;

			return {
				...state,
				recentCreatedId: id,
			};
		}
		case CARD_LIST_DRAG_START: {
			const newDragInProgress = action.payload.cardListIds.reduce(
				(dragInProgress, cardListId) =>
					Object.assign(dragInProgress, {
						[cardListId]: cardListId === action.payload.sourceCardListId,
					}),
				{ ...state.dragState },
			);
			return {
				...state,
				dragState: newDragInProgress,
				isDragging: true,
				isDraggingSubtask: action.payload.sourceCardGroupId !== undefined,
			};
		}

		case CARD_LIST_DRAG_END: {
			return {
				...state,
				dragState: {},
				isDragging: false,
				isDraggingSubtask: false,
			};
		}

		case CARD_GROUP_TOGGLE: {
			const { parentId } = action.payload;

			if (state.cardGroupExpanded[parentId]) {
				return {
					...state,
					cardGroupExpanded: omit(state.cardGroupExpanded, parentId),
				};
			}
			return {
				...state,
				cardGroupExpanded: {
					...state.cardGroupExpanded,
					[parentId]: true,
				},
			};
		}
		case SET_COLLAPSED_CARD_GROUP: {
			return {
				...state,
				cardGroupCollapsed: zipObject(
					action.payload.parentIds,
					Array(action.payload.parentIds.length).fill(true),
				),
			};
		}
		case CLEAR_COLLAPSED_CARD_GROUP: {
			const { parentId } = action.payload;
			return {
				...state,
				cardGroupCollapsed: omit(state.cardGroupCollapsed, parentId),
			};
		}
		case SET_EXPANDED_CARD_GROUP: {
			const expandedCardGroup = [
				...Object.keys(state.cardGroupExpanded),
				...action.payload.parentIds,
			];
			return {
				...state,
				cardGroupExpanded: zipObject(expandedCardGroup, Array(expandedCardGroup.length).fill(true)),
			};
		}
		case CLEAR_EXPANDED_CARD_GROUP: {
			const { parentId } = action.payload;
			return {
				...state,
				cardGroupExpanded: omit(state.cardGroupExpanded, parentId),
			};
		}
		case COLLAPSE_ALL_CARD_GROUPS: {
			return {
				...state,
				cardGroupExpanded: {},
			};
		}
		default: {
			const _exhaustiveCheck: never = action;
			return state;
		}
	}
};
